/**
 * A value that settles `delay` ms after it stops changing.
 *
 * The palette and the `[[` menu both need this (`070` §4, §7.1): a query per keystroke is
 * a query per keystroke, and 150 ms is the sweet spot for a local Postgres — below ~100 ms
 * you fire one per character; above ~250 ms it feels laggy.
 *
 * Deliberately a debounced VALUE, not a debounced callback (`useDebouncedCallback` already
 * exists for that). The query string is what TanStack Query keys on, so the debounce has to
 * live on the key: debouncing the fetch instead would leave a `queryKey` changing on every
 * keystroke, one cache entry per character, and `keepPreviousData` with nothing to keep.
 *
 * `immediate` is the escape hatch for the empty query — clearing the box must show the
 * recent-pages list at once, not 150 ms later.
 */
import { useEffect, useState } from 'react';

export function useDebouncedValue<T>(value: T, delay: number, immediate = false): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (immediate) {
      setSettled(value);
      return;
    }

    const timer = setTimeout(() => {
      setSettled(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay, immediate]);

  return settled;
}
